/**
 * Support Session Constants
 *
 * Constants for managing support account sessions that allow
 * admin users to temporarily access organisations.
 */

export const SUPPORT_SESSION_STATUS = {
    /** Request submitted, awaiting approval from org */
    PENDING: 'pending',
    /** Request approved, support account has access */
    APPROVED: 'approved',
    /** Request rejected by org */
    REJECTED: 'rejected',
    /** Session has expired (automatic) */
    EXPIRED: 'expired',
    /** Session manually revoked by org */
    REVOKED: 'revoked',
} as const;

export const SUPPORT_SESSION_SOURCE = {
    /** Session requested through the admin console and approved by the tenant */
    ADMIN_REQUEST: 'admin_request',
    /** Fixed-duration session provisioned by trusted organisation-onboarding code */
    ONBOARDING: 'onboarding',
} as const;

export type SupportSessionSource = (typeof SUPPORT_SESSION_SOURCE)[keyof typeof SUPPORT_SESSION_SOURCE];

export type SupportSessionStatus = (typeof SUPPORT_SESSION_STATUS)[keyof typeof SUPPORT_SESSION_STATUS];

export const SUPPORT_SESSION_STATUS_LABELS: Record<SupportSessionStatus, string> = {
    pending: 'Pending Approval',
    approved: 'Active',
    rejected: 'Rejected',
    expired: 'Expired',
    revoked: 'Revoked',
} as const;

/**
 * Statuses that indicate an active/usable session
 */
export const ACTIVE_SUPPORT_SESSION_STATUSES: SupportSessionStatus[] = [SUPPORT_SESSION_STATUS.APPROVED] as const;

/**
 * Statuses that indicate the session is no longer active
 */
export const INACTIVE_SUPPORT_SESSION_STATUSES: SupportSessionStatus[] = [
    SUPPORT_SESSION_STATUS.REJECTED,
    SUPPORT_SESSION_STATUS.EXPIRED,
    SUPPORT_SESSION_STATUS.REVOKED,
] as const;

/**
 * Statuses that allow approval (only pending)
 */
export const APPROVABLE_SUPPORT_SESSION_STATUSES: SupportSessionStatus[] = [SUPPORT_SESSION_STATUS.PENDING] as const;

/**
 * Statuses that allow revocation (only approved)
 */
export const REVOCABLE_SUPPORT_SESSION_STATUSES: SupportSessionStatus[] = [SUPPORT_SESSION_STATUS.APPROVED] as const;

/**
 * Default session duration in days when creating a new support session request
 */
export const DEFAULT_SUPPORT_SESSION_DURATION_DAYS = 7;

/**
 * Maximum allowed duration for tenant-approved support-session requests.
 */
export const MAX_SUPPORT_SESSION_DURATION_DAYS = 90;

/**
 * Fixed duration for the trusted, auto-approved onboarding support session.
 *
 * This explicit product exception does not pass through the request/approval APIs and remains
 * tenant-visible and revocable. Requested support sessions are still capped by
 * MAX_SUPPORT_SESSION_DURATION_DAYS.
 */
export const ONBOARDING_SUPPORT_SESSION_DURATION_DAYS = 183;
